import React, { useState, ReactElement, useEffect } from 'react';
import styled from 'styled-components';
import { useDeepCompare } from '../../hooks';
import { SystemIconButton } from '../Icon/SystemIconButton';
import { Checkbox } from '../Checkbox/Checkbox';
import { Typography } from '../Typography/Typography';
import { Embolden } from '../Embolden';
import { Grid } from '../Grid/Grid';
import { Paper } from '../Paper/Paper';
import { Uds } from '../../types';
import { useIntlUds } from '../../translations/UdsIntlProvider';
import {
  getLeftOptions,
  getNewChecked,
  getNewOptions,
  getRightOptions,
  shouldHideCheckbox
} from './TransferList.utils';

export interface TransferListOption<VALUE extends string> {
  value: VALUE;
  label: string;
}

export type TransferListProps<VALUE extends string> = Uds<{
  /*! @default: 250px */
  paperWidth?: string;
  /*! @default: 300px */
  paperHeight?: string;
  options: TransferListOption<VALUE>[];
  values: VALUE[];
  /* use this value to filter values */
  searchValue?: string;
  onChange: (values: VALUE[]) => void;
}>;

export const TransferList = <VALUE extends string>({
  paperWidth = '250px',
  paperHeight = '300px',
  options = [],
  values = [],
  searchValue = '',
  dataTest = 'transfer-list',
  onChange
}: TransferListProps<VALUE>): ReactElement | null => {
  const { intl } = useIntlUds();

  const [checkedRight, setCheckedRight] = useState<VALUE[]>([]);
  const [checkedLeft, setCheckedLeft] = useState<VALUE[]>([]);
  const [leftOptions, setLeftOptions] = useState<TransferListOption<VALUE>[]>(getLeftOptions(options, values));
  const [rightOptions, setRightOptions] = useState<TransferListOption<VALUE>[]>(getRightOptions(options, values));

  const hasOptionsChanged = useDeepCompare(options);
  const hasValuesChanged = useDeepCompare(values);
  const rightOptionsHasChanged = useDeepCompare(rightOptions);

  useEffect(() => {
    if (rightOptionsHasChanged) {
      onChange(rightOptions.map((option) => option.value));
    }
  }, [onChange, rightOptions, rightOptionsHasChanged]);

  useEffect(() => {
    if (hasOptionsChanged || hasValuesChanged) {
      setLeftOptions(getLeftOptions(options, values));
      setRightOptions(getRightOptions(options, values));
    }
  }, [options, values, hasValuesChanged, hasOptionsChanged]);

  const handleToggleRight = (value: VALUE) => () => {
    const newChecked = getNewChecked(checkedRight, value);
    setCheckedRight(newChecked);
  };

  const handleToggleLeft = (value: VALUE) => () => {
    const newChecked = getNewChecked(checkedLeft, value);
    setCheckedLeft(newChecked);
  };

  const checkAllLeft = () => {
    setCheckedLeft((prevState) => {
      if (prevState.length === leftOptions.length) return [];
      return leftOptions.map((option) => option.value);
    });
  };

  const checkAllRight = () => {
    setCheckedRight((prevState) => {
      if (prevState.length === rightOptions.length) return [];
      return rightOptions.map((option) => option.value);
    });
  };

  const handleCheckedRight = () => {
    const newRightOptions = [...rightOptions, ...leftOptions.filter((option) => checkedLeft.includes(option.value))];
    const newLeftOptions = getNewOptions(leftOptions, checkedLeft);
    setRightOptions(newRightOptions);
    setLeftOptions(newLeftOptions);
    setCheckedRight([]);
    setCheckedLeft([]);
  };

  const handleCheckedLeft = () => {
    const newLeftOptions = [...leftOptions, ...rightOptions.filter((option) => checkedRight.includes(option.value))];
    const newRightOptions = getNewOptions(rightOptions, checkedRight);
    setLeftOptions(newLeftOptions);
    setRightOptions(newRightOptions);
    setCheckedLeft([]);
    setCheckedRight([]);
  };

  return (
    <Grid dataTest={dataTest} container spacing={2} justifyContent="center" alignItems="center">
      <Grid item>
        <CheckboxList
          direction="left"
          options={leftOptions}
          disabled={leftOptions.length === 0 || !!searchValue}
          checked={checkedLeft}
          paperWidth={paperWidth}
          paperHeight={paperHeight}
          handleToggle={handleToggleLeft}
          checkAll={checkAllLeft}
          searchValue={searchValue}
          dataTest={dataTest}
        />
      </Grid>
      <Grid item>
        <Grid container direction="column" alignItems="center">
          <SystemIconButton
            onClick={handleCheckedRight}
            color={checkedLeft.length !== 0 ? 'active' : undefined}
            disabled={checkedLeft.length === 0}
            dataTest={`${dataTest}-move-to-right`}
            iconName="keyboard_arrow_right"
            aria-label={intl.TransferList.moveSelectedRightBtn}
          />
          <SystemIconButton
            onClick={handleCheckedLeft}
            color={checkedRight.length !== 0 ? 'active' : undefined}
            disabled={checkedRight.length === 0}
            dataTest={`${dataTest}-move-to-left`}
            iconName="keyboard_arrow_left"
            aria-label={intl.TransferList.moveSelectedLeftBtn}
          />
        </Grid>
      </Grid>
      <Grid item>
        <CheckboxList
          direction="right"
          options={rightOptions}
          checked={checkedRight}
          disabled={rightOptions.length === 0 || !!searchValue}
          paperWidth={paperWidth}
          paperHeight={paperHeight}
          handleToggle={handleToggleRight}
          checkAll={checkAllRight}
          searchValue={searchValue}
          dataTest={dataTest}
        />
      </Grid>
    </Grid>
  );
};

interface CheckboxListProps<VALUE extends string> {
  options: TransferListOption<VALUE>[];
  checked: VALUE[];
  direction: 'right' | 'left';
  paperWidth: string;
  disabled: boolean;
  paperHeight: string;
  handleToggle: (value: VALUE) => () => void;
  checkAll: () => void;
  searchValue: string;
  dataTest: string;
}

const CheckboxList = <VALUE extends string>({
  disabled,
  options,
  direction,
  paperWidth,
  paperHeight,
  checked,
  handleToggle,
  searchValue,
  dataTest,
  checkAll
}: CheckboxListProps<VALUE>): ReactElement | null => (
  <StyledPaper dataTest={`${dataTest}-${direction}-list`} $paperWidth={paperWidth} $paperHeight={paperHeight}>
    <StyledListHeader>
      <Checkbox
        dataTest={`${dataTest}-${direction}-check-all`}
        disabled={disabled}
        checked={options.length > 0 && options.length === checked.length}
        onChange={checkAll}
      >
        <Typography variant="body1">{direction === 'left' ? 'Available fields' : 'Chosen fields'}</Typography>
        <Typography variant="body2">
          {checked.length}/{options.length} selected
        </Typography>
      </Checkbox>
    </StyledListHeader>
    {options.map(({ value, label }) => (
      <StyledCheckboxWrapper key={value} $hide={shouldHideCheckbox(searchValue, label)}>
        <Checkbox
          dataTest={`transfer-list-checkbox-${value}`}
          checked={checked.indexOf(value) !== -1}
          onChange={handleToggle(value)}
        >
          <Typography variant="body1">
            <Embolden term={searchValue}>{label}</Embolden>
          </Typography>
        </Checkbox>
      </StyledCheckboxWrapper>
    ))}
  </StyledPaper>
);

const StyledCheckboxWrapper = styled.div<{ $hide: boolean }>`
  padding: ${({ theme }) => theme.spacingSize.m} ${({ theme }) => theme.spacingSize.l};
  display: ${({ $hide }) => ($hide ? 'none' : 'block')};
`;

const StyledListHeader = styled.div`
  border-bottom: 1px solid ${({ theme }) => theme.colorSurfaceVariant};
  padding: ${({ theme }) => theme.spacingSize.l};
  position: sticky;
  z-index: 1;
  background-color: ${(props) => props.theme.colorSurface};
  color: ${(props) => props.theme.colorOnSurface};
  top: 0;
`;

const StyledPaper = styled(Paper)<{ $paperWidth: string; $paperHeight: string }>`
  width: ${({ $paperWidth }) => $paperWidth};
  height: ${({ $paperHeight }) => $paperHeight};
  overflow: auto;
`;
